import SparkMD5 from 'spark-md5';

/**
 * STEL-1440: Sapotan Video Attachment
 * Module xử lý validate + nén video trước khi upload.
 * Tái sử dụng ở FormBug.js và ContactDetail.js.
 */

// Giới hạn thời lượng tối đa (giây)
const VIDEO_MAX_DURATION = 180;

// Giới hạn kích thước sau nén (bytes): 150MB
const VIDEO_MAX_SIZE_BYTES = 150 * 1024 * 1024;

// Độ phân giải nén target
const VIDEO_COMPRESS_WIDTH = 1280;
const VIDEO_COMPRESS_HEIGHT = 720;

export const VIDEO_ERROR_MESSAGES = {
  browser_not_supported: 'お使いのブラウザは動画アップロードに対応していません。',
  invalid_format: '.mp4、.mov形式の動画をアップロードしてください。',
  duration_exceeded: '180秒以内の動画をアップロードしてください。',
  size_exceeded: '150MB以下のファイルをアップロードしてください。',
};

/**
 * Đọc metadata của video file (duration + resolution).
 * @param {File} file
 * @returns {Promise<{ duration: number, width: number, height: number }>}
 */
const getVideoMetadata = (file) => {
  return new Promise((resolve, reject) => {
    const videoEl = document.createElement('video');
    videoEl.preload = 'metadata';
    const objectUrl = URL.createObjectURL(file);
    videoEl.src = objectUrl;
    videoEl.addEventListener('loadedmetadata', () => {
      const meta = {
        duration: Math.round(videoEl.duration),
        width: videoEl.videoWidth,
        height: videoEl.videoHeight,
      };
      URL.revokeObjectURL(objectUrl);
      resolve(meta);
    });
    videoEl.addEventListener('error', () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Cannot read video metadata'));
    });
  });
};

/**
 * Tính md5Sum của Blob.
 * @param {Blob} blob
 * @returns {Promise<string>} md5 hex string
 */
const computeMd5 = (blob) => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    const spark = new SparkMD5.ArrayBuffer();
    fileReader.onload = (event) => {
      spark.append(event.target.result);
      resolve(spark.end());
    };
    fileReader.onerror = reject;
    fileReader.readAsArrayBuffer(blob);
  });
};

/**
 * Nén video sang 720p MP4 dùng MediaRecorder + Canvas.
 * @param {File} file - file video gốc
 * @param {Function} onProgress - callback(percent: number)
 * @returns {Promise<Blob>} compressed video blob
 */
const compressVideo = (file, onProgress) => {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const videoEl = document.createElement('video');
    videoEl.src = objectUrl;
    videoEl.muted = true; // Tắt audio output ra loa — captureStream vẫn lấy được audio track
    videoEl.playsInline = true;

    videoEl.addEventListener('loadedmetadata', () => {
      const { videoWidth: w, videoHeight: h } = videoEl;
      const scaleW = w > VIDEO_COMPRESS_WIDTH ? VIDEO_COMPRESS_WIDTH / w : 1;
      const scaleH = h > VIDEO_COMPRESS_HEIGHT ? VIDEO_COMPRESS_HEIGHT / h : 1;
      const scale = Math.min(scaleW, scaleH);
      const canvasW = Math.round(w * scale);
      const canvasH = Math.round(h * scale);

      const canvas = document.createElement('canvas');
      canvas.width = canvasW;
      canvas.height = canvasH;
      const ctx = canvas.getContext('2d');

      // Canvas stream cho video track (resized)
      const canvasStream = canvas.captureStream(25);

      // Lấy audio track từ video element trực tiếp
      // captureStream() trên video element trả cả audio + video tracks gốc
      let combinedStream;
      try {
        const videoStream = videoEl.captureStream ? videoEl.captureStream() : videoEl.mozCaptureStream();
        const audioTracks = videoStream.getAudioTracks();

        if (audioTracks.length > 0) {
          // Kết hợp: video từ canvas (resized) + audio từ video element (nguyên gốc)
          combinedStream = new MediaStream([
            ...canvasStream.getVideoTracks(),
            ...audioTracks,
          ]);
        } else {
          combinedStream = canvasStream;
        }
      } catch (e) {
        // Fallback: không có audio
        combinedStream = canvasStream;
      }

      const mimeType = MediaRecorder.isTypeSupported('video/mp4')
        ? 'video/mp4'
        : 'video/webm';
      const recorder = new MediaRecorder(combinedStream, { mimeType });
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = () => {
        videoEl.pause();
        videoEl.src = '';
        URL.revokeObjectURL(objectUrl);
        const blob = new Blob(chunks, { type: mimeType });
        resolve(blob);
      };

      recorder.onerror = (e) => {
        videoEl.pause();
        videoEl.src = '';
        URL.revokeObjectURL(objectUrl);
        reject(e.error || new Error('MediaRecorder error'));
      };

      // Vẽ frame liên tục lên canvas (resize)
      const drawFrame = () => {
        if (videoEl.paused || videoEl.ended) return;
        ctx.drawImage(videoEl, 0, 0, canvasW, canvasH);
        requestAnimationFrame(drawFrame);
      };

      // Progress tracking
      const duration = videoEl.duration;
      let progressInterval = null;
      if (onProgress) {
        progressInterval = setInterval(() => {
          const pct = duration > 0
            ? Math.min(Math.round((videoEl.currentTime / duration) * 100), 99)
            : 0;
          onProgress(pct);
        }, 500);
      }

      videoEl.addEventListener('ended', () => {
        if (progressInterval) clearInterval(progressInterval);
        if (onProgress) onProgress(100);
        recorder.stop();
      });

      recorder.start(100);
      videoEl.play();
      drawFrame();
    });

    videoEl.addEventListener('error', () => {
      videoEl.pause();
      videoEl.src = '';
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Cannot load video for compression'));
    });

    videoEl.load();
  });
};

/**
 * Xử lý video file: validate → nén → check size → tính md5.
 *
 * @param {File} file - file được chọn
 * @param {{ onProgress: Function, onError: Function, onSuccess: Function }} callbacks
 *   - onProgress(percent: number)
 *   - onError(errorCode: string)  -- dùng với VIDEO_ERROR_MESSAGES
 *   - onSuccess({ compressedBlob: Blob, duration: number, md5Sum: string })
 */
export const processVideoFile = async (file, callbacks) => {
  const { onProgress, onError, onSuccess } = callbacks;

  // 1. Validate MIME (lớp bảo vệ thứ 2 — selectFile đã check trước)
  if (file.type !== 'video/mp4' && file.type !== 'video/quicktime') {
    onError('invalid_format');
    return;
  }

  // 2. Feature detection
  if (!window.MediaRecorder) {
    onError('browser_not_supported');
    return;
  }

  try {
    // 3. Đọc metadata (duration + resolution)
    const meta = await getVideoMetadata(file);

    // 4. Validate duration
    if (meta.duration > VIDEO_MAX_DURATION) {
      onError('duration_exceeded');
      return;
    }

    // 5. Option 1: Skip nén nếu video đã đạt chuẩn (≤720p và ≤150MB)
    // So theo cạnh dài/ngắn để hỗ trợ cả landscape (1280×720) lẫn portrait (720×1280)
    const longEdge = Math.max(meta.width, meta.height);
    const shortEdge = Math.min(meta.width, meta.height);
    const isWithinResolution =
      longEdge <= VIDEO_COMPRESS_WIDTH && shortEdge <= VIDEO_COMPRESS_HEIGHT;
    const isWithinSize = file.size <= VIDEO_MAX_SIZE_BYTES;

    if (isWithinResolution && isWithinSize) {
      // Không cần nén → dùng file gốc luôn (nhanh hơn nhiều)
      if (onProgress) onProgress(100);
      const md5Sum = await computeMd5(file);
      onSuccess({ compressedBlob: file, duration: meta.duration, md5Sum });
      return;
    }

    // 6. Nén 720p (chỉ khi cần)
    const compressedBlob = await compressVideo(file, onProgress);

    // 7. Validate size sau nén
    if (compressedBlob.size > VIDEO_MAX_SIZE_BYTES) {
      onError('size_exceeded');
      return;
    }

    // 8. Tính md5Sum
    const md5Sum = await computeMd5(compressedBlob);

    // 9. Thành công
    onSuccess({ compressedBlob, duration: meta.duration, md5Sum });
  } catch (err) {
    onError('browser_not_supported');
  }
};

// Exported for testing only
export { getVideoMetadata as _getVideoMetadata, compressVideo as _compressVideo, computeMd5 as _computeMd5 };
